import { useTranslation } from "react-i18next";
import { Manager, validatePassword } from "../../../Manager";
import { fetchUsersForManager, type User } from "../../../../state/users";
import { supabase } from "../../../../lib/supabase";
import { useCallback } from "react";
import { useAuth } from "../../../../hooks/useAuth";
import { Box } from "@radix-ui/themes";

function validateEmail(email: string): boolean {
  // Simple regex for recognizable email
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
}

export function ManageUsers() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  const fetchData = useCallback(() => fetchUsersForManager(), []);

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: User | null,
  ) => {
    if (item) {
      // Update existing user - only send changed fields
      const updates: Record<string, string> = { userId: item.id };

      if (data.email !== (item.email ?? "")) {
        updates.email = data.email as string;
      }
      if (data.full_name !== item.full_name) {
        updates.fullName = data.full_name as string;
      }
      if (data.role !== (item.user_roles?.role ?? "")) {
        updates.role = data.role as string;
      }

      const response = await supabase.functions.invoke("update-user", {
        body: updates,
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to update user");
      }

      if (response.data?.error) {
        throw new Error(response.data.error);
      }
    } else {
      // Create new user
      const response = await supabase.functions.invoke("create-user", {
        body: {
          email: data.email as string,
          password: data.password as string,
          fullName: data.full_name as string,
          role: data.role as string,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to create user");
      }

      if (response.data?.error) {
        throw new Error(response.data.error);
      }
    }
  };

  const handleDelete = async (user: User) => {
    // Don't allow deleting yourself
    if (user.id === currentUser?.id) {
      throw new Error(t("users.cannotDeleteSelf"));
    }

    const response = await supabase.functions.invoke("delete-user", {
      body: { userId: user.id },
    });

    if (response.error) {
      throw new Error(response.error.message || "Failed to delete user");
    }

    if (response.data?.error) {
      throw new Error(response.data.error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box p="4">
      <Manager.Root<User> title={t("users.title")} fetchData={fetchData}>
        <Manager.CreateButton label={t("users.createUser")} />

        <Manager.Table>
          <Manager.Column<User>
            header={t("users.fullName")}
            accessor={(u) => u.full_name}
          />
          <Manager.Column<User>
            header={t("users.email")}
            accessor={(u) => u.email ?? "—"}
          />
          <Manager.Column<User>
            header={t("users.role")}
            accessor={(u) => u.user_roles?.role ?? "—"}
          />
          <Manager.Column<User>
            header={t("users.createdAt")}
            accessor={(u) => formatDate(u.created_at)}
          />
          <Manager.Actions>
            <Manager.EditAction />
            <Manager.DeleteAction />
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<User>
          title={(isEdit) =>
            isEdit ? t("users.editUser") : t("users.createUser")
          }
          description={(isEdit) =>
            isEdit ? t("users.editDescription") : t("users.createDescription")
          }
          onSubmit={handleSubmit}
          validate={(data) => {
            const emailValid = validateEmail(data.email as string);

            // Validation rules:
            // - Full name, email, and role are always required
            // - Password is only required for creation (when it exists in the form)
            // - If password exists, it must be valid
            const hasPassword =
              data.password && (data.password as string).trim() !== "";
            const passwordValid = hasPassword
              ? validatePassword(data.password as string).valid
              : true;

            return (
              (data.full_name as string)?.trim() !== "" &&
              (data.email as string)?.trim() !== "" &&
              emailValid &&
              passwordValid &&
              (data.role as string) !== ""
            );
          }}
        >
          <Manager.TextField
            name="full_name"
            label={t("users.fullName")}
            required
            placeholder={t("users.fullNamePlaceholder")}
            getValue={(item) => (item as User).full_name}
          />

          <Manager.TextField
            name="email"
            label={t("users.email")}
            required
            placeholder={t("users.emailPlaceholder")}
            getValue={(item) => (item as User).email ?? ""}
          />

          <Manager.PasswordField hideInEditMode />

          <Manager.SelectField
            name="role"
            label={t("users.role")}
            options={[
              { value: "rep", label: t("users.roleRep") },
              { value: "auditor", label: t("users.roleAuditor") },
              { value: "admin", label: t("users.roleAdmin") },
            ]}
            getOptionLabel={(opt: { value: string; label: string }) =>
              opt.label
            }
            getOptionValue={(opt: { value: string; label: string }) =>
              opt.value
            }
            getValue={(item) => (item as User).user_roles?.role ?? ""}
          />
        </Manager.FormDialog>

        <Manager.DeleteDialog<User>
          getItemName={(u) => u.full_name}
          onDelete={handleDelete}
        />
      </Manager.Root>
    </Box>
  );
}
